import VSelect from 'vue-select';
import {createNamespacedHelpers} from "vuex";
import api from "@modules/AudienceManager/api";
import {bootstrapDangerGrowl as dangerGrowl} from "@core/Util/growler";
import {optionsToValueMap} from "@modules/AudienceManager/Util";

const {mapGetters, mapMutations} = createNamespacedHelpers('audienceManager/builder');

/**
 * Invoke function after timeout expires, only the last attempt is invoked. Each new attempt resets the timeout.
 * @param callback Function to invoke.
 * @param delay Delay for function invocation in milliseconds.
 * @returns {function(...[*]=)} Debounce wrapped function.
 */
const debounce = (callback, delay) => {
    let timeoutRef;
    
    //We can't use an arrow function for we need access to the contextual arguments variable
    return function() {
        //Cancel previous attempt when present
        clearTimeout(timeoutRef);
        
        timeoutRef = setTimeout(
            () => {
                timeoutRef = undefined;
                callback(...arguments);
            }, 
            delay
        );
    };
};

export default {
    name: "MultiSelectFilterQuestion",
    components: {
        VSelect
    },
    created(){
        //Debounce causes our function to lose it's 'this' value;
        //thus we need to bind it once an actual instance of our component has been created
        this.fetchOptions = debounce(this.fetchOptions.bind(this), 500);
    },
    methods: {
        /**
         * Extract the value from our given option
         *
         * @param {Object} option selected option.
         * @param {string} option.value Option value.
         * @returns {string}
         */
        reduceSelectedOption: option => option.value,

        /**
         * Triggered when the search text changes.
         *
         * @param {String} search Current search text
         * @param {function(loading: Boolean)} loading Toggle loading class
         */
        fetchOptions(search, loading){
            loading(true);

            api.getFilterOptions(this.filterType.value, search).then(
                questionOptions => {
                    this.questionOptions = questionOptions;
                    loading(false);
                },
                err => {
                    dangerGrowl(err);
                    loading(false);
                }
            );
        },
        ...mapMutations({
            commitValueMutation(commit, mutation, value) {
                commit(mutation, {
                    questionId: this.questionId,
                    value: value || undefined
                });
            },
            setExternalValueLabels: 'setExternalValueLabels'
        })
    },
    computed: {
        canSelectValue(){
            return !!this.filterType;
        },
        questionValue: {
            get(){
                const selectedValue = this.storeQuestionValue(this.questionId) || [];
                const options = optionsToValueMap(
                    this.storeOptions(this.questionId)
                );
                
                //When labels differ from values; we don't want to lose our values
                return selectedValue.map(v => options.get(v) || {label: v, value: v});
            },
            set(options){
                //Our value hasn't been reduced to `just` the value property
                //the value label needs to be persisted alongside the value
                
                this.commitValueMutation(
                    'setQuestionValue',
                    options.map(o => o.value)
                );
                
                this.setExternalValueLabels({
                    questionId: this.questionId,
                    options: options
                });
            }
        },
        filterType: {
            get(){
                const options = optionsToValueMap(this.filterTypeOptions);

                const selectedValue = this.storeFilterValue(this.questionId);
                return options.get(selectedValue);
            },
            set(value){
                this.commitValueMutation('setQuestionFilterValue', value);

                //Our filter changed thus we should clear our value
                this.questionValue = [];
            }
        },
        filterTypeOptions(){
            return this.storeFilterOptions(this.questionId);
        },
        ...mapGetters({
            'storeFilterOptions' :'questionFilterOptions',
            'storeOptions': 'questionOptions',
            'storeQuestionValue' :'questionValue',
            'storeFilterValue': 'questionFilterValue',
            'canEdit': 'canEditAudienceQuestionAnswers'
        })
    },
    data: () => ({
        /**
         * @type {Array<{label: string, value: string}>}
         */
        questionOptions: []
    }),
    props: {
        questionId: {
            type: String,
            required: true
        }
    }
};